import {
  Fragment,
  useId,
  useRef,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
  type ThHTMLAttributes,
} from 'react';
import {
  Button as AriaButton,
  Menu,
  MenuItem,
  MenuTrigger,
  Popover,
  type Selection,
} from 'react-aria-components';
import { cx } from './cx';
import { EmptyState } from './EmptyState';
import type { EmptyArtName } from './EmptyArt';
import { Icon } from './Icon';
import { tableCards, useCardRows, type CardPlace } from './tableCards';
import {
  directionWords,
  sortLabel,
  type ColumnSort,
  type SortColumn,
  type SortDir,
  type TableSort,
} from './tableSort';
import { useScrollRegion } from './useScrollRegion';
import f from './fields.module.css';
import s from './DataTable.module.css';

const TONE = { problem: s.toneProblem, done: s.toneDone, none: undefined } as const;

export interface DataColumn<Row> {
  id: string;
  /** Column heading (sentence case; shown uppercase). */
  header: string;
  /** Renders the cell for one row. */
  cell: (row: Row) => ReactNode;
  /** Numbers are right-aligned with tabular figures. */
  numeric?: boolean;
  /** Keep the cell on one line. */
  nowrap?: boolean;
  /**
   * In card rows (`cards`): `title` heads the card, `action` sits at the bottom at full width;
   * everything else is a value under its column's name.
   */
  card?: CardPlace;
  /**
   * The column sorts (APP-149 D3): its heading becomes a button when the table has
   * `onSortChange`. `value` is what `sortRows` compares; `words` name the two directions.
   */
  sort?: ColumnSort<Row>;
}

export interface DataTableEmpty {
  /** Short bold headline: "Nothing in the queue." */
  title: string;
  /** One sentence on what will appear and when. */
  body: string;
  /** The art for the card rows' empty state (default: an award). */
  art?: EmptyArtName;
}

/**
 * Details under a row, opened in place (APP-109): a toggle leads the row, and the details span
 * every column under it (a card of their own, joined to the row's, in card rows). The page keeps
 * which rows are open, so it can open them all at once.
 */
export interface DataTableDetails<Row> {
  /** Whether the row has details; a row without gets no toggle. */
  has: (row: Row) => boolean;
  /** The row's details, rendered only while open. */
  of: (row: Row) => ReactNode;
  /** What the toggle names, for a screen reader: "SDB-2026-0145". */
  name: (row: Row) => string;
  /** The open rows, by `rowKey`. */
  open: ReadonlySet<string>;
  onOpenChange: (next: ReadonlySet<string>) => void;
}

export interface DataTableProps<Row> {
  /** Names the table for screen readers (and the scroll region). */
  caption: string;
  columns: readonly DataColumn<Row>[];
  rows: readonly Row[];
  rowKey: (row: Row, index: number) => string;
  /** Shown in the single empty row. The header row always stays. */
  empty: DataTableEmpty;
  /**
   * A designed empty state of the page's own (a day one or caught-up message) in place of
   * `empty`'s words: in the one empty row under the header row, and alone in card rows. One
   * empty message for one table, never two.
   */
  emptyState?: ReactNode;
  /** Minimum table width before it scrolls sideways inside its card. */
  minWidth?: number;
  /**
   * Below 720 px, show each row as a card of labelled values with its action at the bottom,
   * instead of a table that scrolls sideways (QA-030). Off by default.
   */
  cards?: boolean;
  /**
   * A totals row under the rows, by column id ("3 people × $1,500", "$4,500"). Columns without one
   * stay blank. Not shown while the table is empty. In card rows it's the last card.
   */
  total?: Partial<Record<string, ReactNode>>;
  /** Marks a row (an over-budget line, a pending top-up). Pair it with words: never colour alone. */
  rowTone?: (row: Row) => 'problem' | 'done' | null;
  /** Details each row opens in place (APP-109). */
  details?: DataTableDetails<Row>;
  /** The column the rows are sorted by, and which way (APP-149 D3); see `onSortChange`. */
  sort?: TableSort;
  /**
   * Turns sorting on: each column with `sort` gets a heading button. The first press sorts it
   * ascending and the next flips it; the active heading carries `aria-sort`. The table never
   * reorders its rows: the page sorts the whole list (`sortRows`) before it pages it. In card rows,
   * a "Sort by" menu above the cards takes the headings' place. Left out, nothing changes.
   */
  onSortChange?: (next: TableSort) => void;
}

const ARIA_SORT = { asc: 'ascending', desc: 'descending' } as const;
const SORT_ICON = { asc: 'arrowUp', desc: 'arrowDown' } as const;
const DIRS: readonly SortDir[] = ['asc', 'desc'];

/** A "Sort by" choice's key: the direction, then the column (whose id may hold anything). */
const sortKey = (sort: TableSort): string => `${sort.dir}:${sort.column}`;

function sortOfKey(key: string): TableSort | null {
  const at = key.indexOf(':');
  const dir = key.slice(0, at);
  return dir === 'asc' || dir === 'desc' ? { dir, column: key.slice(at + 1) } : null;
}

export interface SortHeadProps extends Omit<ThHTMLAttributes<HTMLTableCellElement>, 'children'> {
  /** The column's id: what the sort names. */
  column: string;
  sort: TableSort | undefined;
  onSortChange: (next: TableSort) => void;
  /** A heading over numbers: its arrow goes before its words, over the numbers' right edge. */
  numeric?: boolean | undefined;
  /** Card rows hide the headings, so it's words only there; the "Sort by" menu sorts instead. */
  plain?: boolean;
  /**
   * The cell's own side padding when it isn't DataTable's 12 px, so the button still covers the
   * whole cell and its words stay where the column's start.
   */
  pad?: number;
  /** The heading's words (a hidden "Estimate " before "Cash" is part of them). */
  children: ReactNode;
}

/**
 * A sortable column heading (APP-149 D3): a button that sorts by the column, ascending first and
 * then the other way, with the direction as the th's `aria-sort` and an arrow. DataTable draws its
 * headings with it, and a table built by hand (a grouped or banded one) uses it the same way.
 */
export function SortHead({
  column,
  sort,
  onSortChange,
  numeric,
  plain = false,
  pad,
  className,
  style,
  children,
  ...th
}: SortHeadProps): ReactElement {
  const dir = sort?.column === column ? sort.dir : null;
  const own: CSSProperties | undefined = plain
    ? style
    : {
        ...style,
        paddingBlock: 0,
        ...(pad !== undefined && ({ '--sort-head-pad': `${String(pad)}px` } as CSSProperties)),
      };
  return (
    <th
      scope="col"
      className={cx(className, numeric === true && s.num, !plain && s.sortHead)}
      style={own}
      {...(dir !== null && { 'aria-sort': ARIA_SORT[dir] })}
      {...th}
    >
      {plain ? (
        children
      ) : (
        // Named by its words alone (label in name, APP-144); the direction is the th's aria-sort.
        <AriaButton
          className={cx(s.sortButton, dir !== null && s.sortActive)}
          onPress={() => {
            onSortChange({ column, dir: dir === 'asc' ? 'desc' : 'asc' });
          }}
        >
          {children}
          <Icon
            name={dir === null ? 'sortable' : SORT_ICON[dir]}
            size={14}
            strokeWidth={dir === null ? 2 : 2.5}
            className={s.sortIcon}
          />
        </AriaButton>
      )}
    </th>
  );
}

/**
 * Card rows have no headings to press, so a menu takes their place (D3): it shows the sort in
 * force, and lists each sortable column both ways. A table built by hand puts it above its cards.
 */
export function SortByMenu<Row>({
  columns,
  rows,
  sort,
  onSortChange,
}: {
  columns: readonly SortColumn<Row>[];
  rows: readonly Row[];
  sort: TableSort | undefined;
  onSortChange: (next: TableSort) => void;
}): ReactElement {
  const sortable = columns.filter((column) => column.sort !== undefined);
  const current =
    sort !== undefined && sortable.some((column) => column.id === sort.column) ? sort : null;
  return (
    <div className={s.sortBy}>
      <MenuTrigger>
        <AriaButton className={s.sortByButton}>
          <span>Sort by{current !== null && `: ${sortLabel(columns, current, rows)}`}</span>
          <Icon name="chevronDown" size={16} />
        </AriaButton>
        <Popover className={f.popover} placement="bottom start">
          <Menu
            className={f.listbox}
            selectionMode="single"
            disallowEmptySelection
            selectedKeys={current === null ? [] : [sortKey(current)]}
            onSelectionChange={(keys: Selection) => {
              if (keys === 'all') return;
              const [key] = [...keys];
              const next = key === undefined ? null : sortOfKey(String(key));
              if (next !== null) onSortChange(next);
            }}
          >
            {sortable.flatMap((column) =>
              DIRS.map((dir) => {
                const words = `${column.header}, ${directionWords(column, dir, rows)}`;
                return (
                  <MenuItem
                    key={sortKey({ column: column.id, dir })}
                    id={sortKey({ column: column.id, dir })}
                    textValue={words}
                    className={f.option}
                  >
                    {words}
                  </MenuItem>
                );
              }),
            )}
          </Menu>
        </Popover>
      </MenuTrigger>
    </div>
  );
}

/**
 * A table that keeps its header row when empty (with one empty-row slot), right-aligns numbers,
 * and scrolls horizontally inside its card instead of stretching the page. With `cards`, it shows
 * card rows on narrow screens, with a designed empty state of their own.
 */
export function DataTable<Row>({
  caption,
  columns,
  rows,
  rowKey,
  empty,
  emptyState,
  minWidth,
  cards = false,
  total,
  rowTone,
  details,
  sort,
  onSortChange,
}: DataTableProps<Row>): ReactElement {
  const scrollRef = useRef<HTMLDivElement>(null);
  const region = useScrollRegion(scrollRef, caption);
  const narrow = useCardRows();
  const panels = useId();
  const c = tableCards(cards && narrow);
  const showTotal = total !== undefined && rows.length > 0;
  // Rows that open details lead with the toggle's own column.
  const span = columns.length + (details === undefined ? 0 : 1);
  const panelId = (row: Row, index: number): string => `${panels}details-${rowKey(row, index)}`;
  const isOpen = (row: Row, index: number): boolean =>
    details !== undefined && details.has(row) && details.open.has(rowKey(row, index));
  const sorts = onSortChange !== undefined && columns.some((column) => column.sort !== undefined);

  /** A column's heading: its name, or with sorting on, a button that sorts by it (D3). */
  const heading = (column: DataColumn<Row>): ReactElement =>
    onSortChange !== undefined && column.sort !== undefined ? (
      <SortHead
        key={column.id}
        column={column.id}
        sort={sort}
        onSortChange={onSortChange}
        numeric={column.numeric}
        plain={c.on}
        {...c.header}
      >
        {column.header}
      </SortHead>
    ) : (
      <th key={column.id} scope="col" className={cx(column.numeric && s.num)} {...c.header}>
        {column.header}
      </th>
    );

  /** The toggle that leads a row: a 44 px chevron in the table, "Show details" on a card. */
  const toggle = (row: Row, index: number): ReactElement | null => {
    if (details === undefined) return null;
    const key = rowKey(row, index);
    const open = isOpen(row, index);
    return (
      <td {...c.cell('', 'toggle', c.on ? undefined : s.toggleCell)}>
        {details.has(row) && (
          <AriaButton
            className={cx(s.toggle, c.on && s.toggleText)}
            aria-expanded={open}
            aria-controls={panelId(row, index)}
            aria-label={`${open ? 'Hide' : 'Show'} details for ${details.name(row)}`}
            onPress={() => {
              const next = new Set(details.open);
              if (open) next.delete(key);
              else next.add(key);
              details.onOpenChange(next);
            }}
          >
            {c.on && <span aria-hidden="true">{open ? 'Hide details' : 'Show details'}</span>}
            <Icon name={open ? 'chevronUp' : 'chevronDown'} size={18} />
          </AriaButton>
        )}
      </td>
    );
  };

  if (c.on && rows.length === 0)
    return emptyState !== undefined ? (
      <>{emptyState}</>
    ) : (
      <EmptyState
        variant="first-day"
        art={empty.art ?? 'award'}
        size="sm"
        headline={empty.title}
        body={empty.body}
      />
    );

  const table = (
    <div ref={scrollRef} className={s.scroll} {...(c.on ? {} : region)}>
      <table
        {...c.table(s.table)}
        style={minWidth === undefined || c.on ? undefined : { minWidth }}
      >
        <caption className={s.caption}>{caption}</caption>
        <thead {...c.head}>
          <tr {...c.row}>
            {details !== undefined && (
              <th scope="col" className={s.toggleCell} {...c.header}>
                <span className="visually-hidden">Details</span>
              </th>
            )}
            {columns.map(heading)}
          </tr>
        </thead>
        <tbody {...c.body}>
          {rows.length === 0 ? (
            <tr className={cx(s.emptyRow, emptyState !== undefined && s.emptyRowState)}>
              <td colSpan={span}>
                {emptyState ?? (
                  <>
                    <span className={s.emptyTitle}>{empty.title}</span> {empty.body}
                  </>
                )}
              </td>
            </tr>
          ) : (
            rows.map((row, index) => (
              <Fragment key={rowKey(row, index)}>
                <tr
                  {...c.row}
                  className={cx(TONE[rowTone?.(row) ?? 'none'], isOpen(row, index) && s.openRow)}
                >
                  {toggle(row, index)}
                  {columns.map((column) => (
                    <td
                      key={column.id}
                      {...c.cell(
                        column.header,
                        column.card ?? 'value',
                        cx(column.numeric && s.num, column.nowrap && s.nowrap),
                      )}
                    >
                      {column.cell(row)}
                    </td>
                  ))}
                </tr>
                {details?.has(row) === true && (
                  <tr {...c.row} className={s.detailRow} hidden={!isOpen(row, index)}>
                    <td id={panelId(row, index)} colSpan={span} {...c.details}>
                      {isOpen(row, index) && details.of(row)}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))
          )}
          {showTotal && c.on && (
            <tr {...c.row} className={s.totalRow}>
              {details !== undefined && <td {...c.cell('', 'value')} />}
              {columns.map((column) => (
                <td
                  key={column.id}
                  {...c.cell(column.header, column.card === 'title' ? 'title' : 'value')}
                >
                  {total[column.id]}
                </td>
              ))}
            </tr>
          )}
        </tbody>
        {showTotal && !c.on && (
          <tfoot>
            <tr className={s.totalRow}>
              {details !== undefined && <td />}
              {columns.map((column) => (
                <td key={column.id} className={cx(column.numeric && s.num)}>
                  {total[column.id]}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );

  return sorts && c.on ? (
    <>
      <SortByMenu columns={columns} rows={rows} sort={sort} onSortChange={onSortChange} />
      {table}
    </>
  ) : (
    table
  );
}
